import { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
export const AUTH_COOKIE_CONFIG = {
    name: 'agencyAuthToken',
    secure: true, // HTTPS only in production
    sameSite: 'strict', // CSRF protection
    httpOnly: false, // Allow JS access for logout
    maxAge: 24 * 60 * 60, // 24 hours
    path: '/'
};
/**
 * Decodes the authentication token and returns the payload
 * @param {string} token - The authentication token to decode.
 * @returns {Nullable<AdminAuthTokenPayload>} The payload of the authentication token.
 */
export const decodeAuthToken = (token) => {
    if (!token)
        throw new Error('No token provided');
    try {
        const decodedTokenString = atob(token);
        const parts = decodedTokenString.split('.');
        if (parts.length !== 3)
            throw new Error('Invalid token format');
        const payload = jwtDecode(decodedTokenString);
        return payload;
    }
    catch {
        return null;
    }
};
/**
 * Validates if a token exists and is not expired
 * @param {string | null} token - The authentication token to validate.
 * @returns {boolean} Whether the token is valid.
 */
export const isTokenValid = (token) => {
    if (!token)
        return false;
    try {
        const payload = decodeAuthToken(token);
        if (!payload)
            throw new Error('Invalid token');
        const now = Math.floor(Date.now() / 1000);
        return payload.exp > now;
    }
    catch {
        return false;
    }
};
/**
 * Sets an authentication cookie with enterprise security settings
 * @param {string} token - The authentication token to set.
 * @param {Partial<CookieConfig>} [config] - The configuration for the cookie.
 */
export const setAuthCookie = (token, config = {}) => {
    const finalConfig = { ...AUTH_COOKIE_CONFIG, ...config };
    const cookieValue = `${finalConfig.name}=${token}`;
    const secure = finalConfig.secure ? '; secure' : '';
    const sameSite = `; samesite=${finalConfig.sameSite}`;
    const maxAge = `; max-age=${finalConfig.maxAge}`;
    const path = `; path=${finalConfig.path}`;
    document.cookie = `${cookieValue}${secure}${sameSite}${maxAge}${path}`;
};
/**
 * Gets the authentication token from cookies
 * @param {string} [cookieName] - The name of the cookie to get.
 * @returns {Nullable<string>} The authentication token.
 */
export const getAuthCookie = (cookieName = AUTH_COOKIE_CONFIG.name) => {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(row => row.startsWith(`${cookieName}=`));
    return cookie ? cookie.split('=')[1] : null;
};
/**
 * Removes the authentication cookie
 * @param {string} [cookieName] - The name of the cookie to remove.
 */
export const removeAuthCookie = (cookieName = AUTH_COOKIE_CONFIG.name) => {
    document.cookie = `${cookieName}=; max-age=0; path=${AUTH_COOKIE_CONFIG.path}`;
};
/**
 * Hybrid token management - cookies primary, sessionStorage fallback
 * @returns {Nullable<string>} The authentication token.
 */
export const getAuthToken = () => {
    const cookieToken = getAuthCookie();
    const sessionToken = sessionStorage.getItem('agencyAuthToken');
    if (cookieToken && cookieToken !== sessionToken) {
        sessionStorage.setItem('agencyAuthToken', cookieToken);
        return cookieToken;
    }
    // If no cookie but sessionStorage exists, use sessionStorage
    // If both exist and match, use cookie
    return cookieToken || sessionToken;
};
/**
 * Sets token in both cookie and sessionStorage for reliability
 * @param {Nullable<string>} token - The authentication token to set.
 */
export const setAuthToken = (token) => {
    if (token) {
        setAuthCookie(token);
        sessionStorage.setItem('agencyAuthToken', token);
    }
    else {
        removeAuthCookie();
        sessionStorage.removeItem('agencyAuthToken');
    }
};
/**
 * Gets the current admin from the API
 * @returns The current admin
 */
/**
 *
 * @param {string} token - The authentication token.
 * @param {AdminAuthTokenPayload} tokenPayload - The payload of the authentication token.
 * @param {import('../../../../reducers/adminsReducer').CurrentAdmin} currentAdmin - The current admin.
 * @returns {AuthSession} The session.
 */
export const assembleSession = (token, tokenPayload, currentAdmin) => ({
    adminId: tokenPayload.admin_id,
    token,
    tokenVerified: !!currentAdmin?.id,
    expiresAt: tokenPayload.exp * 1000 || Date.now() + 8 * 60 * 60 * 1000, // 8 hours default
    agencyId: tokenPayload.agency_id ?? null,
    organizationId: tokenPayload.organization_id ?? null,
    refreshToken: null
});
/**
 *
 * @param {import('../../../../reducers/adminsReducer').CurrentAdmin} currentAdmin - The current admin.
 * @returns {AuthPermissions} The permissions.
 */
export const assemblePermissions = (currentAdmin) => {
    if (currentAdmin?.is_super_admin)
        sessionStorage.setItem('admin_permissions', 'false');
    else
        sessionStorage.setItem('admin_permissions', JSON.stringify(currentAdmin?.admin_permissions));
    return {
        permissions: currentAdmin?.is_super_admin || !currentAdmin?.admin_permissions ? false : currentAdmin?.admin_permissions,
        isSuperAdmin: currentAdmin?.is_super_admin ?? false,
        isClientAdmin: currentAdmin?.is_client_admin ?? false
    };
};
/**
 *
 * @param {unknown} err
 * @returns {SliceError} The error message.
 */
export const handleErrorMessage = (err) => {
    let message = 'Login failed';
    let action = null;
    if (err instanceof AxiosError) {
        const errorCode = err.response?.data?.error_code;
        action = errorCode;
        if (!errorCode)
            message = err.response?.data?.error || 'Email or password invalid.';
        if (errorCode === 'black_listed')
            message = 'Not Authorized IP.';
        if (errorCode === 'not_active')
            message = `Your account is not active. ${err.response?.data?.message ?? ''}`;
        if (errorCode === 'token_required')
            message = `Please enter 2 factor authentication Token. ${err.response?.data?.message ?? ''}`;
        if (errorCode === 'api_access_only')
            message = err.response?.data?.message ?? 'API access only';
    }
    return { message, action };
};
